import * as React from "react";
import { Body1, Caption1, makeStyles, tokens, Link } from "@fluentui/react-components";
import { AppEventBus } from "../../services/appEventBus";
import { AlertEntry } from "../../services/workbookAnalyticsRulesEngine";
import AlertCard from "./AlertCard";
import DismissedAlertCard from "./DismissedAlertCard";
import { rulesEngine } from "../index";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
  },
  header: {
    padding: "16px",
    paddingBottom: "8px",
  },
  alertList: {
    padding: "0 16px 16px 16px",
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
  },
  emptyState: {
    padding: "16px",
    textAlign: "center",
  },
  section: {
    marginBottom: "16px",
  },
  sectionHeader: {
    marginBottom: "8px",
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightSemibold,
  }
});

interface AnalysisTabProps {
  classifiedWorksheetIds: Set<string>;
  alerts: Record<string, AlertEntry>;
  currentWorksheetId: string | undefined;
  filterType: 'workbook' | 'worksheet';
  onFilterChange: (type: 'workbook' | 'worksheet') => void;
}

const AnalysisTab: React.FC<AnalysisTabProps> = ({ classifiedWorksheetIds, alerts, currentWorksheetId, filterType, onFilterChange }) => {
  const styles = useStyles();

  const handleDismissAlert = (alertId: string, reasonCode: string, description?: string) => {
    const context = description ? { description } : undefined;
    rulesEngine.dismissAlert(alertId, reasonCode, context);
  };

  const handleRestoreAlert = (alertId: string) => {
    rulesEngine.restoreAlert(alertId);
  };

  // Separate active and dismissed alerts
  const allActiveAlerts = Object.values(alerts).filter((alert) => !alert.dismissal);
  const allDismissedAlerts = Object.values(alerts).filter((alert) => alert.dismissal);

  const activeAlerts = filterType === 'worksheet' && currentWorksheetId
    ? allActiveAlerts.filter(a => a.sheetId === currentWorksheetId)
    : allActiveAlerts;
    
  const dismissedAlerts = filterType === 'worksheet' && currentWorksheetId
    ? allDismissedAlerts.filter(a => a.sheetId === currentWorksheetId)
    : allDismissedAlerts;

  const otherWorksheetAlertsCount = allActiveAlerts.length - activeAlerts.length;
  const isCurrentWorksheetClassified = currentWorksheetId && classifiedWorksheetIds.has(currentWorksheetId);

  let emptyStateMessage: React.ReactNode = <Body1>Aμto doesn't have any items that need your attention</Body1>;

  if (filterType === 'worksheet') {
    if (!isCurrentWorksheetClassified) {
      if (otherWorksheetAlertsCount > 0) {
        emptyStateMessage = (
          <Body1>
            Aμto doesn't recognise this worksheet so has no insights to offer. However there are <Link as="button" onClick={() => onFilterChange('workbook')}>{otherWorksheetAlertsCount} items</Link> for review across other worksheets.
          </Body1>
        );
      } else {
        emptyStateMessage = <Body1>Aμto doesn't recognise this worksheet so has no insights to offer</Body1>;
      }
    } else {
      // Recognized worksheet
      if (otherWorksheetAlertsCount > 0) {
        emptyStateMessage = (
          <Body1>
            Aμto doesn't have any items that need your attention for this worksheet however there are <Link as="button" onClick={() => onFilterChange('workbook')}>{otherWorksheetAlertsCount} items</Link> for review across other worksheets.
          </Body1>
        );
      }
    }
  }

  return (
    <div className={styles.container}>
      {activeAlerts.length === 0 && dismissedAlerts.length === 0 ? (
        <div className={styles.emptyState}>
          {emptyStateMessage}
        </div>
      ) : (
        <div className={styles.alertList}>
          {activeAlerts.length > 0 && (
            <div className={styles.section}>
              {activeAlerts.map((alert) => (
                <AlertCard
                  key={alert.id}
                  alert={alert}
                  onDismiss={handleDismissAlert}
                />
              ))}
            </div>
          )}
          {dismissedAlerts.length > 0 && (
            <div className={styles.section}>
              <Caption1 className={styles.sectionHeader}>Dismissed</Caption1>
              {dismissedAlerts.map((alert) => (
                <DismissedAlertCard
                  key={alert.id}
                  alert={alert}
                  onRestore={handleRestoreAlert}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AnalysisTab;
